import { useEffect, useState, useCallback } from "react";
import { CheckCircle, XCircle, AlertCircle, Info, X } from "lucide-react";

export type NotificationType = "success" | "error" | "warning" | "info";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number; // en milisegundos, 0 = no auto-close
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationProps {
  notification: Notification;
  onRemove: (id: string) => void;
}

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

const colors = {
  success: "bg-white border-l-4 border-[#282828] text-gray-900 shadow-lg",
  error: "bg-white border-l-4 border-red-600 text-gray-900 shadow-lg",
  warning: "bg-white border-l-4 border-yellow-500 text-gray-900 shadow-lg",
  info: "bg-white border-l-4 border-blue-600 text-gray-900 shadow-lg",
};

const iconColors = {
  success: "text-[#282828]",
  error: "text-red-600",
  warning: "text-yellow-600",
  info: "text-blue-600",
};

export function NotificationComponent({ notification, onRemove }: NotificationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const Icon = icons[notification.type];

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300); // Duración de la animación de salida
  }, [onRemove, notification.id]);

  useEffect(() => {
    // Animación de entrada - usar requestAnimationFrame para mejor rendimiento
    let rafId: number | undefined;
    let timer: number | undefined;
    
    rafId = requestAnimationFrame(() => {
      timer = window.setTimeout(() => setIsVisible(true), 10);
    });
    
    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    // Auto-close si tiene duración
    if (notification.duration && notification.duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification.duration, handleClose]);

  return (
    <div
      className={`
        max-w-md w-full rounded-lg pointer-events-auto overflow-hidden
        transform transition-all duration-300 ease-in-out
        ${isVisible && !isLeaving 
          ? "translate-x-0 md:translate-x-0 translate-y-0 opacity-100" 
          : "translate-x-[120%] md:translate-x-[120%] translate-y-[-20px] md:translate-y-0 opacity-0"}
        ${colors[notification.type]}
      `}
      style={{
        willChange: 'transform, opacity',
      }}
    >
      <div className="p-3 md:p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`h-5 w-5 md:h-6 md:w-6 ${iconColors[notification.type]}`} />
          </div>
          <div className="ml-3 md:ml-4 w-0 flex-1">
            <p className="text-xs md:text-sm font-semibold text-gray-900">{notification.title}</p>
            {notification.message && (
              <p className="mt-1 md:mt-1.5 text-xs md:text-sm text-gray-600 leading-relaxed">{notification.message}</p>
            )}
            {notification.action && (
              <div className="mt-2 md:mt-3">
                <button
                  onClick={notification.action.onClick}
                  className="text-xs md:text-sm font-medium underline hover:no-underline focus:outline-none"
                >
                  {notification.action.label}
                </button>
              </div>
            )}
          </div>
          <div className="ml-3 md:ml-4 flex-shrink-0 flex">
            <button
              onClick={handleClose}
              className="rounded-md inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
            >
              <span className="sr-only">Cerrar</span>
              <X className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
